import { i18n } from "@lingui/core";

export type AppLocale = "fr" | "en" | "de" | "es";
export const SUPPORTED_LOCALES: AppLocale[] = ["fr", "en", "de", "es"];
const LOCALE_STORAGE_KEY = "mage-x-locale";

export async function loadCatalog(locale: AppLocale) {
  const { messages } = await import(`./locales/${locale}/messages.mjs`);
  i18n.loadAndActivate({ locale, messages });
}

export async function setAppLocale(locale: AppLocale) {
  await loadCatalog(locale);
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

export function getStoredLocale(): AppLocale {
  return (localStorage.getItem(LOCALE_STORAGE_KEY) ?? "fr") as AppLocale;
}

/**
 * The locale the app starts in: the `?lang=` a link carried, otherwise the last one chosen on this
 * browser. The website passes the language its visitor was reading, so a physician coming from the
 * English site lands on an English welcome page — and stays there, the choice being stored.
 */
export function getInitialLocale(): AppLocale {
  const url = new URL(window.location.href);
  const requested = url.searchParams.get("lang");
  if (requested !== null) {
    // Read once, then dropped: left in the address, it would win over a language picked later
    // on the page as soon as that page is reloaded.
    url.searchParams.delete("lang");
    window.history.replaceState(window.history.state, "", url);
  }
  if (requested && (SUPPORTED_LOCALES as string[]).includes(requested)) {
    localStorage.setItem(LOCALE_STORAGE_KEY, requested);
    return requested as AppLocale;
  }
  return getStoredLocale();
}

export { i18n };
